import { DevanaWSClient } from "@devana/ws-tools";
import { marked } from "marked";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getOrCreateWordClient } from "../services/wordClientSingleton";
import { getOrCreatePowerPointClient } from "../services/powerPointClientSingleton";
import { getOrCreateExcelClient } from "../services/excelClientSingleton";
import { buildWordAgentPrompt } from "../prompts/wordAgentPrompt";
import { buildPowerPointAgentPrompt } from "../prompts/powerPointAgentPrompt";
import { buildExcelAgentPrompt } from "../prompts/excelAgentPrompt";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Configuration de marked pour la sécurité et le style
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Parser les tags [tool:start:{...}] et [tool:end:{...}] pour affichage visuel amélioré
function parseToolTags(content: string): string {
  // Fonction pour extraire le JSON complet avec accolades imbriquées et vérifier le crochet fermant
  const extractToolJson = (
    text: string,
    startIndex: number
  ): { json: string; hasClosingBracket: boolean } | null => {
    let braceCount = 0;
    let inString = false;
    let escape = false;
    let jsonStart = -1;

    for (let i = startIndex; i < text.length; i++) {
      const char = text[i];

      if (escape) {
        escape = false;
        continue;
      }

      if (char === "\\") {
        escape = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (inString) continue;

      if (char === "{") {
        if (jsonStart === -1) jsonStart = i;
        braceCount++;
      } else if (char === "}") {
        braceCount--;
        if (braceCount === 0 && jsonStart !== -1) {
          const json = text.substring(jsonStart, i + 1);
          // Vérifier si le caractère suivant est un crochet fermant
          const hasClosingBracket = text[i + 1] === "]";
          return { json, hasClosingBracket };
        }
      }
    }

    return null;
  };

  // Parser les tool:start
  let result = content;
  const startMatches: Array<{
    index: number;
    json: string;
    hasClosingBracket: boolean;
  }> = [];

  let searchIndex = 0;
  while (true) {
    const startPos = result.indexOf("[tool:start:", searchIndex);
    if (startPos === -1) break;

    const jsonStart = startPos + "[tool:start:".length;
    const extracted = extractToolJson(result, jsonStart);

    if (extracted) {
      startMatches.push({
        index: startPos,
        json: extracted.json,
        hasClosingBracket: extracted.hasClosingBracket,
      });
      searchIndex = startPos + extracted.json.length + 20;
    } else {
      searchIndex = startPos + 1;
    }
  }

  // Remplacer en ordre inverse pour ne pas décaler les indices
  for (let i = startMatches.length - 1; i >= 0; i--) {
    const { index, json, hasClosingBracket } = startMatches[i];

    try {
      const toolData = JSON.parse(json);
      const toolName = toolData.name || "unknown";
      const args = toolData.args || {};

      // Icône selon le type de tool
      let icon = "🔧";
      if (toolName.includes("get_")) icon = "📖";
      else if (toolName.includes("search")) icon = "🔍";
      else if (
        toolName.includes("add_") ||
        toolName.includes("insert") ||
        toolName.includes("edit")
      )
        icon = "✏️";
      else if (
        toolName.includes("format") ||
        toolName.includes("style") ||
        toolName.includes("align")
      )
        icon = "🎨";
      else if (toolName.includes("table")) icon = "📊";
      else if (toolName.includes("comment")) icon = "💬";

      // Args preview limité
      const argsPreview = Object.entries(args)
        .slice(0, 2)
        .map(([key, value]) => {
          let val = value;
          if (typeof value === "string" && value.length > 40) {
            val = value.substring(0, 40) + "...";
          } else if (typeof value === "object") {
            val = "[object]";
          }
          return `${key}: ${val}`;
        })
        .join(", ");

      const replacement = `<div style="display: flex; flex-direction: column; gap: 4px; padding: 8px 12px; background: linear-gradient(135deg, rgba(124, 93, 250, 0.08) 0%, rgba(124, 93, 250, 0.12) 100%); border-left: 3px solid #7C5DFA; border-radius: 6px; margin: 8px 0; font-size: 11px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
<div style="display: flex; align-items: center; gap: 6px;">
<span style="font-size: 16px;">${icon}</span>
<span style="font-weight: 600; color: #7C5DFA; font-size: 13px;">${toolName}</span>
<span style="font-size: 10px; background: rgba(124, 93, 250, 0.2); padding: 2px 6px; border-radius: 4px; color: #7C5DFA;">▶ En cours</span>
</div>
${
  argsPreview
    ? `<div style="font-family: monospace; font-size: 10px; color: rgba(255, 255, 255, 0.6); padding-left: 22px; line-height: 1.4;">${argsPreview}</div>`
    : ""
}
</div>\n\n`;

      // Ajuster le calcul du matchLength selon si le crochet fermant est présent
      const matchLength =
        "[tool:start:".length +
        json.length +
        (hasClosingBracket ? "]".length : 0);
      result =
        result.substring(0, index) +
        replacement +
        result.substring(index + matchLength);
    } catch (e) {
      console.error("Error parsing tool:start:", e);
    }
  }

  // Parser les tool:end de manière similaire
  const endMatches: Array<{
    index: number;
    json: string;
    hasClosingBracket: boolean;
  }> = [];
  searchIndex = 0;

  while (true) {
    const endPos = result.indexOf("[tool:end:", searchIndex);
    if (endPos === -1) break;

    const jsonStart = endPos + "[tool:end:".length;
    const extracted = extractToolJson(result, jsonStart);

    if (extracted) {
      endMatches.push({
        index: endPos,
        json: extracted.json,
        hasClosingBracket: extracted.hasClosingBracket,
      });
      searchIndex = endPos + extracted.json.length + 20;
    } else {
      searchIndex = endPos + 1;
    }
  }

  for (let i = endMatches.length - 1; i >= 0; i--) {
    const { index, json, hasClosingBracket } = endMatches[i];

    try {
      const toolData = JSON.parse(json);
      const toolName = toolData.name || "unknown";
      const success = toolData.success !== false;
      const error = toolData.error;

      const replacement = `<div style="display: flex; align-items: center; gap: 6px; padding: 6px 12px; background: ${
        success ? "rgba(72, 199, 116, 0.1)" : "rgba(255, 146, 146, 0.1)"
      }; border-left: 3px solid ${
        success ? "#48C774" : "#FF9292"
      }; border-radius: 6px; margin: 8px 0; font-size: 11px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
<span style="font-size: 14px;">${success ? "✅" : "❌"}</span>
<span style="font-weight: 600; color: ${
        success ? "#48C374" : "#FF9292"
      }; font-size: 12px;">${toolName}</span>
<span style="font-size: 10px; background: ${
        success ? "rgba(72, 199, 116, 0.2)" : "rgba(255, 146, 146, 0.2)"
      }; padding: 2px 6px; border-radius: 4px; color: ${
        success ? "#48C774" : "#FF9292"
      };">${success ? "✓ Terminé" : "✗ Erreur"}</span>
${
  error
    ? `<span style="font-size: 9px; color: rgba(255, 255, 255, 0.5); margin-left: auto;">${error.substring(
        0,
        50
      )}...</span>`
    : ""
}
</div>\n\n`;

      // Ajuster le calcul du matchLength selon si le crochet fermant est présent
      const matchLength =
        "[tool:end:".length +
        json.length +
        (hasClosingBracket ? "]".length : 0);
      result =
        result.substring(0, index) +
        replacement +
        result.substring(index + matchLength);
    } catch (e) {
      console.error("Error parsing tool:end:", e);
    }
  }

  // Fallback pour les anciens formats sans JSON (très rare maintenant)
  result = result.replace(/\[tool:start:(\w+)\]/g, (match, toolName) => {
    return `<div style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; background: rgba(124, 93, 250, 0.15); border: 1px solid rgba(124, 93, 250, 0.3); border-radius: 6px; font-size: 12px; color: #7C5DFA; margin: 4px 0;"><span style="font-weight: 600;">🔧 ${toolName}</span></div>\n\n`;
  });
  result = result.replace(/\[tool:end:\w+\]/g, "");

  // Supprimer les balises [RESULT] et [/RESULT] de l'affichage mais garder le contenu
  result = result.replace(/\[RESULT\]/g, "");
  result = result.replace(/\[\/RESULT\]/g, "");

  return result;
}

interface QuickAction {
  id: string;
  icon: string;
  label: string;
  prompt: string;
}

interface ChatWidgetProps {
  token: string;
  apiUrl: string;
  agentId: string;
  userIdentity?: string;
  hostcli?: string;
  initialPrompt?: string;
  initialContext?: string;
  customQuickActions?: QuickAction[];
  showDebug?: boolean;
  documentName?: string;
  onLogout?: () => void;
  onOpenSettings?: () => void;
}

// Composant pour afficher un message avec Markdown et actions
const MessageItem: React.FC<{
  message: Message;
  onInsert?: (text: string) => void;
  onReplace?: (text: string) => void;
}> = ({ message, onInsert, onReplace }) => {
  const [copied, setCopied] = useState(false);
  const [inserted, setInserted] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleInsert = () => {
    if (onInsert) {
      onInsert(message.content);
      setInserted(true);
      setTimeout(() => setInserted(false), 2000);
    }
  };

  const handleReplace = () => {
    if (onReplace) {
      onReplace(message.content);
      setInserted(true);
      setTimeout(() => setInserted(false), 2000);
    }
  };

  const htmlContent =
    message.role === "assistant"
      ? (marked.parse(parseToolTags(message.content)) as string)
      : message.content;

  return (
    <div
      style={{
        padding: "12px 16px",
        borderRadius: "12px",
        background:
          message.role === "user"
            ? "rgba(124, 93, 250, 0.15)"
            : "rgba(255, 255, 255, 0.05)",
        border:
          message.role === "user"
            ? "1px solid rgba(124, 93, 250, 0.3)"
            : "1px solid rgba(255, 255, 255, 0.1)",
        fontSize: "14px",
        lineHeight: "1.5",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: "8px",
          color: message.role === "user" ? "#7C5DFA" : "#48C774",
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {message.role === "user" ? "Vous" : "Devana"}
      </div>

      {message.role === "assistant" ? (
        <div
          style={{
            wordBreak: "break-word",
          }}
          className="markdown-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      ) : (
        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {message.content}
        </div>
      )}

      {/* Actions */}
      {message.role === "assistant" && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "8px",
            opacity: 0.7,
            transition: "opacity 0.2s",
            flexWrap: "wrap",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
        >
          <button
            onClick={copyToClipboard}
            style={{
              background: "rgba(124, 93, 250, 0.15)",
              border: "1px solid rgba(124, 93, 250, 0.3)",
              borderRadius: "6px",
              color: copied ? "#48C774" : "#7C5DFA",
              padding: "4px 8px",
              fontSize: "12px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title="Copier dans le presse-papier"
          >
            {copied ? "✓ Copié" : "📋 Copier"}
          </button>

          {onInsert && (
            <button
              onClick={handleInsert}
              style={{
                background: "rgba(72, 199, 116, 0.15)",
                border: "1px solid rgba(72, 199, 116, 0.3)",
                borderRadius: "6px",
                color: inserted ? "#48C774" : "#48C774",
                padding: "4px 8px",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              title="Insérer dans le document"
            >
              {inserted ? "✓ Inséré" : "📝 Insérer"}
            </button>
          )}

          {onReplace && (
            <button
              onClick={handleReplace}
              style={{
                background: "rgba(255, 146, 146, 0.15)",
                border: "1px solid rgba(255, 146, 146, 0.3)",
                borderRadius: "6px",
                color: inserted ? "#48C774" : "#FF9292",
                padding: "4px 8px",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              title="Remplacer la sélection"
            >
              {inserted ? "✓ Remplacé" : "🔄 Remplacer"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ChatWidget: React.FC<ChatWidgetProps> = ({
  token,
  apiUrl,
  agentId,
  userIdentity,
  hostcli,
  initialPrompt,
  initialContext,
  customQuickActions = [],
  showDebug = false,
  documentName: customDocumentName,
  onLogout,
  onOpenSettings,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [selectionWordCount, setSelectionWordCount] = useState(0);
  const [includeFullDocument, setIncludeFullDocument] = useState(false);
  const [fullDocumentWordCount, setFullDocumentWordCount] = useState(0);
  const [wordSessionId, setWordSessionId] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [wsConnectionError, setWsConnectionError] = useState<string | null>(
    null
  );
  const [wsConnectionAttempts, setWsConnectionAttempts] = useState(0);
  const [logs, setLogs] = useState<
    Array<{ time: string; level: string; message: string }>
  >([]);
  const [wordClient, setWordClient] = useState<DevanaWSClient | null>(null);
  const [sessions, setSessions] = useState<Array<any>>([]);
  const [documentName, setDocumentName] = useState<string>("Document Word");
  const [debugInfo, setDebugInfo] = useState<any>(null); // État pour stocker les infos debug
  const [showDebugModal, setShowDebugModal] = useState(false); // État pour afficher/masquer le modal debug
  const [currentDebugFunction, setCurrentDebugFunction] = useState<string>(""); // Fonction actuellement testée
  const [debugLoading, setDebugLoading] = useState(false); // État de chargement pour les tests debug
  const wordClientRef = useRef<DevanaWSClient | null>(null); // Ref pour avoir toujours la dernière valeur
  const sessionIdRef = useRef<string | null>(null); // Ref pour le sessionId aussi
  const isInitializingRef = useRef<boolean>(false); // Flag pour éviter doubles init
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasAutoSentRef = useRef(false);
  const selectionCheckIntervalRef = useRef<number | null>(null);
  const prevAgentIdRef = useRef<string>(agentId);

  // Actions rapides par défaut
  const defaultQuickActions: QuickAction[] = [
    {
      id: "default-1",
      icon: "✍️",
      label: "Améliorer",
      prompt: "Améliore ce texte en le rendant plus clair et professionnel:",
    },
    {
      id: "default-2",
      icon: "🔍",
      label: "Résumer",
      prompt: "Résume ce texte de manière concise:",
    },
    {
      id: "default-3",
      icon: "🌐",
      label: "Traduire EN",
      prompt: "Traduis ce texte en anglais:",
    },
    {
      id: "default-4",
      icon: "🇫🇷",
      label: "Traduire FR",
      prompt: "Traduis ce texte en français:",
    },
    {
      id: "default-5",
      icon: "📧",
      label: "Email pro",
      prompt: "Réécris ce texte sous forme d'email professionnel:",
    },
    {
      id: "default-6",
      icon: "💡",
      label: "Simplifier",
      prompt: "Simplifie ce texte pour qu'il soit facile à comprendre:",
    },
    {
      id: "default-7",
      icon: "📝",
      label: "Points clés",
      prompt: "Extrais les points clés de ce texte sous forme de liste:",
    },
    {
      id: "default-8",
      icon: "🎯",
      label: "Correction",
      prompt: "Corrige l'orthographe et la grammaire de ce texte:",
    },
  ];

  // Combiner les actions par défaut avec les actions personnalisées
  const allQuickActions = [...defaultQuickActions, ...customQuickActions];

  // Fonction pour ajouter un log (sans appeler console.log pour éviter la récursion)
  const addLog = useCallback(
    (level: "info" | "error" | "success", message: string) => {
      const time = new Date().toLocaleTimeString();
      setLogs((prev) => [...prev.slice(-999), { time, level, message }]); // Garder max 1000 logs
    },
    []
  );

  // Intercepter les console.log pour l'UI
  useEffect(() => {
    const originalLog = console.log;
    const originalError = console.error;

    console.log = (...args) => {
      const message = args.join(" ");
      // Intercepter tous les logs avec des tags
      if (message.match(/\[[\w_-]+\]/)) {
        const level = message.includes("✅")
          ? "success"
          : message.includes("❌")
          ? "error"
          : "info";
        const cleanMessage = message.replace(/\[[\w_-]+\]\s*/g, "").trim();
        if (cleanMessage) {
          addLog(level, cleanMessage);
        }
      }
      originalLog.apply(console, args);
    };

    console.error = (...args) => {
      const message = args.join(" ");
      if (message.match(/\[[\w_-]+\]/)) {
        const cleanMessage = message.replace(/\[[\w_-]+\]\s*/g, "").trim();
        if (cleanMessage) {
          addLog("error", cleanMessage);
        }
      }
      originalError.apply(console, args);
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
    };
  }, [addLog]);

  // Initialiser la connexion WebSocket au démarrage
  useEffect(() => {
    if (!token || !apiUrl) {
      setWsConnectionError("Token ou API URL manquant");
      addLog("error", "Token ou API URL manquant");
      return;
    }

    const initWebSocket = async () => {
      // Utiliser une ref pour éviter les doubles init même en StrictMode
      if (isInitializingRef.current) {
        console.log("[ChatWidget] Already initializing, skipping...");
        return;
      }

      if (wordClientRef.current?.isConnected()) {
        console.log("[ChatWidget] Already connected, skipping...");
        return;
      }

      isInitializingRef.current = true;
      try {
        setWsConnectionAttempts((prev) => prev + 1);
        setWsConnectionError(null);

        // Récupérer le nom du document
        let docName = customDocumentName || "Document Word";

        // Si pas de nom personnalisé, essayer de récupérer automatiquement
        if (!customDocumentName && Office?.context?.document) {
          try {
            await new Promise<void>((resolve) => {
              Office.context.document.getFilePropertiesAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  const url = result.value.url;
                  console.log("[ChatWidget] Document URL:", url);

                  if (url) {
                    // Extraire le nom du fichier depuis l'URL
                    const filename = url.split("/").pop()?.split("\\").pop();
                    if (filename) {
                      docName = filename
                        .replace(".docx", "")
                        .replace(".doc", "");
                      console.log("[ChatWidget] Extracted filename:", docName);
                    }
                  }
                }
                resolve();
              });
            });
          } catch (e) {
            console.error("[ChatWidget] Error getting file properties:", e);
          }
        }

        setDocumentName(docName);
        addLog("info", `Connexion (doc: ${docName})...`);

        // Décoder le token JWT pour extraire userId
        let userId: string | undefined;
        try {
          const tokenParts = token.split(".");
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userId = payload.id || payload.userId || payload.sub;
            console.log("[ChatWidget] Extracted userId from token:", userId);
          }
        } catch (e) {
          console.warn("[ChatWidget] Could not extract userId from token");
        }

        // Fonction pour rafraîchir les sessions
        const refreshSessions = async () => {
          try {
            const response = await fetch(`${apiUrl}/sessions/list`, {
              headers: { "x-session-id": sessionIdRef.current || "" },
            });
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.sessions) {
                setSessions(data.sessions);
                console.log(
                  "[ChatWidget] Sessions updated:",
                  data.sessions.length
                );
              }
            }
          } catch (e) {
            console.error("[ChatWidget] Failed to refresh sessions:", e);
          }
        };

        // Détecter si on est dans Word, PowerPoint ou Excel
        const hostType = Office.context.host;
        const isWord = hostType === Office.HostType.Word;
        const isPowerPoint = hostType === Office.HostType.PowerPoint;
        const isExcel = hostType === Office.HostType.Excel;

        console.log(`[ChatWidget] Detected host: ${hostType} (Word: ${isWord}, PowerPoint: ${isPowerPoint}, Excel: ${isExcel})`);

        // Utiliser le singleton approprié selon l'hôte
        let client;
        if (isWord) {
          client = getOrCreateWordClient(apiUrl, {
            documentName: docName,
            bearerToken: token,
            callbacks: {
              onConnected: (sessionId: string) => {
                  setWordSessionId(sessionId);
                  sessionIdRef.current = sessionId;
                  setWsConnected(true);
                  setWsConnectionError(null);
                  addLog("success", `✅ Connecté! Session: ${sessionId}`);
                  refreshSessions(); // Rafraîchir au connect
                },
                onReconnected: (sessionId: string) => {
                  setWordSessionId(sessionId);
                  sessionIdRef.current = sessionId;
                  setWsConnected(true);
                  setWsConnectionError(null);
                  addLog("success", `🔄 Reconnecté! Session: ${sessionId}`);
                  refreshSessions();
                },
                onDisconnected: () => {
                  setWsConnected(false);
                  addLog("info", "⚠️ Déconnecté");
                },
                onError: (error: Error) => {
                  setWsConnected(false);
                  setWsConnectionError(error.message);
                  addLog("error", `❌ Erreur: ${error.message}`);
                },
                onSessionsChanged: () => {
                  addLog("info", "🔄 Sessions mises à jour");
                  refreshSessions(); // Rafraîchir automatiquement
                },
              },
            });
        } else if (isPowerPoint) {
          client = getOrCreatePowerPointClient(apiUrl, {
              documentName: docName,
              bearerToken: token,
              callbacks: {
                onConnected: (sessionId: string) => {
                  setWordSessionId(sessionId);
                  sessionIdRef.current = sessionId;
                  setWsConnected(true);
                  setWsConnectionError(null);
                  addLog("success", `✅ Connecté! Session: ${sessionId}`);
                  refreshSessions(); // Rafraîchir au connect
                },
                onReconnected: (sessionId: string) => {
                  setWordSessionId(sessionId);
                  sessionIdRef.current = sessionId;
                  setWsConnected(true);
                  setWsConnectionError(null);
                  addLog("success", `🔄 Reconnecté! Session: ${sessionId}`);
                  refreshSessions();
                },
                onDisconnected: () => {
                  setWsConnected(false);
                  addLog("info", "⚠️ Déconnecté");
                },
                onError: (error: Error) => {
                  setWsConnected(false);
                  setWsConnectionError(error.message);
                  addLog("error", `❌ Erreur: ${error.message}`);
                },
                onSessionsChanged: () => {
                  addLog("info", "🔄 Sessions mises à jour");
                  refreshSessions(); // Rafraîchir automatiquement
                },
              },
            });
        } else {
          // Excel par défaut
          client = getOrCreateExcelClient(apiUrl, {
            documentName: docName,
            bearerToken: token,
            callbacks: {
              onConnected: (sessionId: string) => {
                setWordSessionId(sessionId);
                sessionIdRef.current = sessionId;
                setWsConnected(true);
                setWsConnectionError(null);
                addLog("success", `✅ Connecté! Session: ${sessionId}`);
                refreshSessions();
              },
              onReconnected: (sessionId: string) => {
                setWordSessionId(sessionId);
                sessionIdRef.current = sessionId;
                setWsConnected(true);
                setWsConnectionError(null);
                addLog("success", `🔄 Reconnecté! Session: ${sessionId}`);
                refreshSessions();
              },
              onDisconnected: () => {
                setWsConnected(false);
                addLog("info", "⚠️ Déconnecté");
              },
              onError: (error: Error) => {
                setWsConnected(false);
                setWsConnectionError(error.message);
                addLog("error", `❌ Erreur: ${error.message}`);
              },
              onSessionsChanged: () => {
                addLog("info", "🔄 Sessions mises à jour");
                refreshSessions();
              },
            },
          });
        }

        // Connecter
        await client.connect();
        setWordClient(client);
        wordClientRef.current = client; // Sauvegarder dans la ref aussi
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Erreur de connexion";
        setWsConnected(false);
        setWsConnectionError(errorMsg);
        addLog("error", `❌ Échec connexion: ${errorMsg}`);
      } finally {
        isInitializingRef.current = false;
      }
    };

    initWebSocket();

    // Cleanup on unmount - NE PAS déconnecter (gardé pour les autres renders)
    return () => {
      console.log("[ChatWidget] Cleanup (keeping connection alive)");
      isInitializingRef.current = false;
      // Ne pas déconnecter le client singleton ici
    };
  }, [token, apiUrl, addLog]);

  // Relancer la connexion quand le nom du document change
  useEffect(() => {
    if (customDocumentName && wordClientRef.current && wordClientRef.current.isConnected()) {
      const docName = customDocumentName;
      setDocumentName(docName);
      addLog("info", `📝 Nom du document mis à jour: ${docName}`);

      // Informer le serveur du changement de nom via un outil spécial ou reconnexion
      // Pour l'instant, on met juste à jour l'état local
      // Le nom sera utilisé dans les prochaines requêtes via buildWordAgentPrompt
    }
  }, [customDocumentName, addLog]);

  // Reset conversation when agent changes
  useEffect(() => {
    if (prevAgentIdRef.current !== agentId) {
      setMessages([]);
      setConversationId(null);
      setStreamedContent("");
      setInput("");
      prevAgentIdRef.current = agentId;
    }
  }, [agentId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fonction pour extraire le contenu entre les balises [RESULT]...[/RESULT]
  const extractResult = (text: string): string => {
    const resultRegex = /\[RESULT\]([\s\S]*?)\[\/RESULT\]/;
    const match = text.match(resultRegex);

    if (match && match[1]) {
      // Retourner le contenu entre les balises, en trimant les espaces
      return match[1].trim();
    }

    // Si pas de balises trouvées, retourner le texte original
    return text;
  };

  // Fonctions pour insérer/remplacer du texte dans le document
  const insertTextInDocument = async (text: string) => {
    // Extraire uniquement le résultat si des balises sont présentes
    const contentToInsert = extractResult(text);
    try {
      const hostType = Office.context.host;

      if (hostType === Office.HostType.Word) {
        await Word.run(async (context) => {
          const selection = context.document.getSelection();
          selection.insertText(contentToInsert, Word.InsertLocation.end);
          await context.sync();
        });
      } else if (hostType === Office.HostType.PowerPoint) {
        Office.context.document.setSelectedDataAsync(contentToInsert, {
          coercionType: Office.CoercionType.Text,
        });
      }
    } catch (error) {
      console.error("Error inserting text:", error);
    }
  };

  const replaceSelectionInDocument = async (text: string) => {
    // Extraire uniquement le résultat si des balises sont présentes
    const contentToReplace = extractResult(text);

    try {
      const hostType = Office.context.host;

      if (hostType === Office.HostType.Word) {
        await Word.run(async (context) => {
          const selection = context.document.getSelection();
          selection.insertText(contentToReplace, Word.InsertLocation.replace);
          await context.sync();
        });
      } else if (hostType === Office.HostType.PowerPoint) {
        Office.context.document.setSelectedDataAsync(contentToReplace, {
          coercionType: Office.CoercionType.Text,
        });
      }
    } catch (error) {
      console.error("Error replacing text:", error);
    }
  };

  const handleQuickAction = async (action: QuickAction) => {
    try {
      const hostType = Office.context.host;
      let selectedText = "";

      if (hostType === Office.HostType.Word) {
        await Word.run(async (context) => {
          const selection = context.document.getSelection();
          selection.load("text");
          await context.sync();
          selectedText = selection.text;
        });
      } else if (hostType === Office.HostType.PowerPoint) {
        await new Promise<void>((resolve) => {
          Office.context.document.getSelectedDataAsync(
            Office.CoercionType.Text,
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                selectedText = String(result.value || "");
              }
              resolve();
            }
          );
        });
      }

      if (selectedText && selectedText.trim()) {
        // Construire le message complet avec le texte sélectionné inclus dans le prompt
        const fullPrompt = `${action.prompt}\n\n${selectedText}`;
        sendMessage(fullPrompt);
      }
    } catch (error) {
      console.error("Error in quick action:", error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamedContent]);

  const sendMessage = useCallback(
    async (message: string, contextText?: string) => {
      if (!message.trim() || isStreaming) return;

      const userMessage: Message = { role: "user", content: message };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setIsStreaming(true);
      setStreamedContent("");

      abortControllerRef.current = new AbortController();

      const fullUrl = `${apiUrl}/v1/chat/completions`;

      // Utiliser les refs pour avoir les dernières valeurs
      const currentClient = wordClientRef.current;
      const currentSessionId = sessionIdRef.current;
      const dynamicTools = currentClient
        ? currentClient.getToolsConfig()
        : undefined;

      console.log("[ChatWidget] Preparing request:");
      console.log("[ChatWidget]   Session ID:", currentSessionId);
      console.log("[ChatWidget]   Word Client:", currentClient ? "Yes" : "No");
      console.log(
        "[ChatWidget]   Dynamic Tools:",
        dynamicTools ? `${dynamicTools.length} tools` : "None"
      );
      if (dynamicTools) {
        console.log(
          "[ChatWidget]   Tools:",
          dynamicTools.map((t) => t.name).join(", ")
        );
      }

      const requestBody: Record<string, unknown> = {
        stream: true,
        messages: [{ role: "user", content: message }],
        model: agentId,
        conversation_id: conversationId || "",
        headers: currentSessionId
          ? { "x-session-id": currentSessionId }
          : undefined,
        dynamicTools,
      };

      // Détecter le type d'hôte et construire le prompt approprié
      const hostType = Office.context.host;
      const isWord = hostType === Office.HostType.Word;
      const isPowerPoint = hostType === Office.HostType.PowerPoint;
      const isExcel = hostType === Office.HostType.Excel;

      let fullIdentity = "";

      if (isWord) {
        // Prompt pour Word
        fullIdentity = buildWordAgentPrompt({
          wordSessionId,
          wsConnected,
          userIdentity
        });
      } else if (isPowerPoint) {
        // Prompt pour PowerPoint
        fullIdentity = buildPowerPointAgentPrompt(
          {
            fileName: documentName || "Présentation",
          },
          undefined, // currentSlideContent - à obtenir si nécessaire
          undefined  // selection - à obtenir si nécessaire
        );

        // Ajouter l'identité utilisateur si définie
        if (userIdentity && userIdentity.trim()) {
          fullIdentity = `${userIdentity}\n\n${fullIdentity}`;
        }
      } else if (isExcel) {
        // Prompt pour Excel
        fullIdentity = buildExcelAgentPrompt(
          {
            fileName: documentName || "Classeur",
          },
          undefined, // selectedRange - à obtenir si nécessaire
          undefined  // cellValues - à obtenir si nécessaire
        );

        // Ajouter l'identité utilisateur si définie
        if (userIdentity && userIdentity.trim()) {
          fullIdentity = `${userIdentity}\n\n${fullIdentity}`;
        }
      }

      // Ajouter le contexte si présent
      if (contextText && contextText.trim().length > 0) {
        fullIdentity += `\n[Contexte additionnel]\n${contextText}\n`;
      }

      if (fullIdentity.trim().length > 0) {
        requestBody.identity = fullIdentity;
      }

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        if (hostcli && hostcli.trim().length > 0) {
          headers["hostcli"] = hostcli;
        }

        const response = await fetch(fullUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          // Essayer de récupérer le message d'erreur du serveur
          let errorDetail = "";
          try {
            const errorData = await response.json();
            errorDetail =
              errorData.error?.message ||
              errorData.message ||
              JSON.stringify(errorData);
          } catch (e) {
            errorDetail = await response.text();
          }

          const error = new Error(
            `Erreur HTTP ${response.status}: ${response.statusText}`
          );
          (error as any).status = response.status;
          (error as any).statusText = response.statusText;
          (error as any).detail = errorDetail;
          throw error;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error("No reader available");
        }

        let buffer = "";
        let fullResponse = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let splitIndex: number;

          while ((splitIndex = buffer.indexOf("\n\n")) !== -1) {
            const messageStr = buffer.slice(0, splitIndex).trim();
            buffer = buffer.slice(splitIndex + 2);

            if (!messageStr.startsWith("data:")) continue;
            if (messageStr === "data: [DONE]") continue;

            try {
              const jsonString = messageStr.replace(/^data:\s*/, "");
              const jsonData = JSON.parse(jsonString);

              if (jsonData?.choices?.[0]?.finish_reason === "stop") {
                continue;
              }

              const tokenContent = jsonData?.choices?.[0]?.delta?.content;
              if (
                tokenContent &&
                tokenContent !== "" &&
                !(fullResponse === "" && tokenContent.trim() === "")
              ) {
                fullResponse += tokenContent;
                setStreamedContent(fullResponse);
              }

              if (jsonData.conversation_id && !conversationId) {
                setConversationId(jsonData.conversation_id);
              }
            } catch (e) {
              console.error("Error parsing stream:", e);
            }
          }
        }

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: fullResponse },
        ]);
        setStreamedContent("");
        setIsStreaming(false);
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Error sending message:", error);

          // Construire un message d'erreur détaillé
          let errorMessage = "❌ **Erreur lors de l'envoi du message**\n\n";

          if (error instanceof Error) {
            errorMessage += `**Type:** ${error.name}\n`;
            errorMessage += `**Message:** ${error.message}\n\n`;
          }

          // Ajouter des détails sur la requête
          errorMessage += `**Détails techniques:**\n`;
          errorMessage += `- API: ${apiUrl}\n`;
          errorMessage += `- Modèle: ${agentId}\n`;

          if ((error as any).status) {
            errorMessage += `- Code HTTP: ${(error as any).status} ${
              (error as any).statusText || ""
            }\n`;
          }

          if ((error as any).detail) {
            errorMessage += `- Détail: ${(error as any).detail}\n`;
          }

          errorMessage += `\n💡 **Suggestions:**\n`;

          // Suggestions contextuelles selon le code d'erreur
          const status = (error as any).status;
          if (status === 401 || status === 403) {
            errorMessage += `- ⚠️ Problème d'authentification : vérifiez votre token\n`;
          } else if (status === 404) {
            errorMessage += `- ⚠️ API introuvable : vérifiez l'URL de l'API\n`;
          } else if (status === 429) {
            errorMessage += `- ⚠️ Trop de requêtes : attendez quelques instants\n`;
          } else if (status >= 500) {
            errorMessage += `- ⚠️ Erreur serveur : le service est temporairement indisponible\n`;
          } else {
            errorMessage += `- Vérifiez votre connexion internet\n`;
            errorMessage += `- Vérifiez que votre token est valide\n`;
          }
          errorMessage += `- Réessayez dans quelques instants\n`;

          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: errorMessage,
            },
          ]);
        }
        setIsStreaming(false);
        setStreamedContent("");
      } finally {
        abortControllerRef.current = null;
      }
    },
    [token, apiUrl, agentId, conversationId, isStreaming, userIdentity, hostcli]
  );

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsStreaming(false);
    if (streamedContent) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: streamedContent },
      ]);
      setStreamedContent("");
    }
  };

  const startNewConversation = () => {
    if (isStreaming) {
      stopGeneration();
    }
    setMessages([]);
    setConversationId(null);
    setStreamedContent("");
    setInput("");
  };

  // Envoyer automatiquement le prompt initial si défini
  useEffect(() => {
    if (initialPrompt && !hasAutoSentRef.current && token) {
      hasAutoSentRef.current = true;
      setTimeout(() => {
        sendMessage(initialPrompt, initialContext || "");
      }, 500);
    }
  }, [initialPrompt, initialContext, token, sendMessage]);

  // Vérifier s'il y a un contexte temporaire depuis les commandes ribbon
  useEffect(() => {
    if (!token || hasAutoSentRef.current) return;

    try {
      const tempContext = Office.context.document.settings.get(
        "devana_temp_context"
      );
      const tempPrompt =
        Office.context.document.settings.get("devana_temp_prompt");

      if (tempContext && tempPrompt) {
        hasAutoSentRef.current = true;

        // Nettoyer les settings temporaires
        Office.context.document.settings.remove("devana_temp_context");
        Office.context.document.settings.remove("devana_temp_prompt");
        Office.context.document.settings.saveAsync();

        // Envoyer le message avec le contexte
        setTimeout(() => {
          sendMessage(tempPrompt, `[Contexte]\n${tempContext}`);
        }, 500);
      }
    } catch (error) {
      console.error("Error checking temp context:", error);
    }
  }, [token, sendMessage]);

  // Fonction pour compter les mots
  const countWords = (text: string): number => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Fonction pour obtenir la sélection actuelle
  const getCurrentSelection = async (): Promise<string> => {
    try {
      const hostType = Office.context.host;
      let selectedText = "";

      if (hostType === Office.HostType.Word) {
        await Word.run(async (context) => {
          const selection = context.document.getSelection();
          selection.load("text");
          await context.sync();
          selectedText = selection.text;
        });
      } else if (hostType === Office.HostType.PowerPoint) {
        await new Promise<void>((resolve) => {
          Office.context.document.getSelectedDataAsync(
            Office.CoercionType.Text,
            (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                selectedText = String(result.value || "");
              }
              resolve();
            }
          );
        });
      }

      return selectedText;
    } catch (error) {
      console.error("Error getting selection:", error);
      return "";
    }
  };

  // Fonction pour obtenir le contenu complet du document
  const getFullDocumentContent = async (): Promise<string> => {
    try {
      const hostType = Office.context.host;
      let fullText = "";

      if (hostType === Office.HostType.Word) {
        await Word.run(async (context) => {
          const body = context.document.body;
          body.load("text");
          await context.sync();
          fullText = body.text;
        });
      } else if (hostType === Office.HostType.PowerPoint) {
        // Pour PowerPoint, on ne peut pas facilement récupérer tout le contenu
        fullText = "[Contenu PowerPoint - non disponible]";
      }

      return fullText;
    } catch (error) {
      console.error("Error getting full document:", error);
      return "";
    }
  };

  // Vérifier périodiquement la sélection pour mettre à jour le compteur
  const updateSelectionWordCount = async () => {
    const selectedText = await getCurrentSelection();
    setSelectionWordCount(countWords(selectedText));
  };

  // Mettre à jour le compteur du document complet
  const updateFullDocumentWordCount = async () => {
    const fullText = await getFullDocumentContent();
    setFullDocumentWordCount(countWords(fullText));
  };

  // Démarrer la vérification périodique de la sélection
  useEffect(() => {
    updateSelectionWordCount(); // Check initial
    updateFullDocumentWordCount(); // Check initial du document

    selectionCheckIntervalRef.current = window.setInterval(() => {
      updateSelectionWordCount();
      updateFullDocumentWordCount();
    }, 2000); // Vérifier toutes les 2 secondes

    return () => {
      if (selectionCheckIntervalRef.current) {
        clearInterval(selectionCheckIntervalRef.current);
      }
    };
  }, []);

  // Fonction pour ouvrir le modal de debug
  const handleDebugClick = () => {
    if (!wordClient || !wordClient.isConnected()) {
      alert("Le client Word n'est pas connecté !");
      return;
    }
    setShowDebugModal(true);
    setDebugInfo(null); // Reset les infos précédentes
    setCurrentDebugFunction("");
  };

  // Fonction pour tester get_document_info
  const testGetDocumentInfo = async () => {
    setDebugLoading(true);
    setCurrentDebugFunction("get_document_info");

    try {
      console.log("[DEBUG] Testing get_document_info...");
      const result = await Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;

        body.load("text");
        paragraphs.load("items");
        await context.sync();

        const totalParagraphs = paragraphs.items.length;
        const totalWords = body.text
          .split(/\s+/)
          .filter((w) => w.length > 0).length;

        // Estimation du nombre de pages
        let totalPages = 0;
        try {
          const fields = body.fields;
          fields.load("items");
          await context.sync();

          for (let field of fields.items) {
            try {
              field.load("code");
              await context.sync();

              if (field.code && field.code.toUpperCase().includes("NUMPAGES")) {
                field.load("result");
                await context.sync();

                if (field.result) {
                  const resultRange = field.result as Word.Range;
                  resultRange.load("text");
                  await context.sync();

                  const resultText = resultRange.text;
                  const match = resultText.match(/\d+/);
                  if (match) {
                    totalPages = parseInt(match[0]);
                    console.log(`[DEBUG] Found NUMPAGES field: ${totalPages} pages`);
                    break;
                  }
                }
              }
            } catch (fieldError) {
              console.log("[DEBUG] Error reading field:", fieldError);
            }
          }
        } catch (e) {
          console.log("[DEBUG] No fields or error reading fields:", e);
        }

        // Si on n'a pas trouvé de champ NUMPAGES, estimer
        if (totalPages === 0) {
          const estimateByWords = Math.ceil(totalWords / 500);
          const estimateByParagraphs = Math.ceil(totalParagraphs / 25);
          const estimateByCharacters = Math.ceil(body.text.length / 3000);

          totalPages = Math.max(estimateByWords, estimateByParagraphs, estimateByCharacters);
          totalPages = Math.max(1, totalPages);

          console.log(`[DEBUG] Page estimation: words=${estimateByWords}, paragraphs=${estimateByParagraphs}, chars=${estimateByCharacters} → ${totalPages} pages`);
        }

        // Aperçu des premiers paragraphes (AVEC les vides pour garder les bons numéros!)
        const previewCount = Math.min(20, totalParagraphs);
        const paragraphPreviews: Array<{
          number: number;
          preview: string;
          isEmpty: boolean;
          style?: string;
        }> = [];

        for (let i = 0; i < previewCount; i++) {
          const para = paragraphs.items[i];
          para.load(["text", "style", "styleBuiltIn"]);
        }
        await context.sync();

        for (let i = 0; i < previewCount; i++) {
          const para = paragraphs.items[i];
          const text = para.text.trim();
          const style = para.style || para.styleBuiltIn || "Normal";

          paragraphPreviews.push({
            number: i + 1,
            preview: text.length > 0
              ? (text.length > 100 ? text.substring(0, 100) + "..." : text)
              : "[Paragraphe vide]",
            isEmpty: text.length === 0,
            style: style
          });
        }

        // Compter aussi les tables
        const tables = context.document.body.tables;
        tables.load("items");
        await context.sync();
        const totalTables = tables.items.length;

        // Compter les lignes - chaque paragraphe est au minimum une ligne
        let totalLines = totalParagraphs;

        // Ajouter les lignes supplémentaires dans les paragraphes longs
        for (let i = 0; i < paragraphs.items.length; i++) {
          const paraText = paragraphs.items[i].text;
          // Estimer ~80 caractères par ligne en moyenne
          const estimatedLinesInPara = Math.max(1, Math.ceil(paraText.length / 80));
          totalLines += (estimatedLinesInPara - 1); // -1 car on a déjà compté 1 ligne par paragraphe
        }

        return {
          totalPages,
          totalParagraphs,
          totalWords,
          totalLines,
          totalTables,
          totalCharacters: body.text.length,
          paragraphPreviews,
          message: `Document contains ${totalPages} page(s), ${totalParagraphs} paragraphs, ${totalWords} words, ${totalLines} lines, ${totalTables} table(s).`,
        };
      });

      console.log("[DEBUG] Result:", result);
      setDebugInfo(result);
    } catch (error) {
      console.error("[DEBUG] Error:", error);
      setDebugInfo({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      setDebugLoading(false);
    }
  };

  // Fonction pour tester get_content
  const testGetContent = async () => {
    setDebugLoading(true);
    setCurrentDebugFunction("get_content");

    try {
      console.log("[DEBUG] Testing get_content (first 10 paragraphs)...");
      const result = await Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;

        body.load("text");
        paragraphs.load("items");
        await context.sync();

        // Limiter à 10 paragraphes pour le test
        const maxPara = Math.min(10, paragraphs.items.length);
        const contentPreview = [];

        for (let i = 0; i < maxPara; i++) {
          const para = paragraphs.items[i];
          para.load(["text", "style", "font"]);
          para.font.load(["name", "size", "bold", "italic", "color"]);
        }
        await context.sync();

        for (let i = 0; i < maxPara; i++) {
          const para = paragraphs.items[i];
          contentPreview.push({
            number: i + 1,
            text: para.text.substring(0, 100),
            style: para.style,
            font: {
              name: para.font.name,
              size: para.font.size,
              bold: para.font.bold,
              italic: para.font.italic,
              color: para.font.color
            }
          });
        }

        return {
          totalParagraphs: paragraphs.items.length,
          preview: contentPreview,
          message: `Showing first ${maxPara} paragraphs with styles`
        };
      });

      console.log("[DEBUG] get_content result:", result);
      setDebugInfo(result);
    } catch (error) {
      console.error("[DEBUG] Error:", error);
      setDebugInfo({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      setDebugLoading(false);
    }
  };

  // Fonction pour tester get_selection
  const testGetSelection = async () => {
    setDebugLoading(true);
    setCurrentDebugFunction("get_selection");

    try {
      console.log("[DEBUG] Testing get_selection...");
      const result = await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load(["text", "font", "style"]);
        selection.font.load(["name", "size", "bold", "italic", "color"]);
        await context.sync();

        return {
          text: selection.text,
          length: selection.text.length,
          style: selection.style,
          font: {
            name: selection.font.name,
            size: selection.font.size,
            bold: selection.font.bold,
            italic: selection.font.italic,
            color: selection.font.color
          },
          message: selection.text.length > 0
            ? `Selection contains ${selection.text.length} characters`
            : "No text currently selected"
        };
      });

      console.log("[DEBUG] get_selection result:", result);
      setDebugInfo(result);
    } catch (error) {
      console.error("[DEBUG] Error:", error);
      setDebugInfo({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      setDebugLoading(false);
    }
  };

  // Fonction pour tester get_table_info
  const testGetTableInfo = async () => {
    setDebugLoading(true);
    setCurrentDebugFunction("get_table_info");

    try {
      console.log("[DEBUG] Testing get_table_info...");
      const result = await Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items");
        await context.sync();

        const tableInfos = [];
        for (let i = 0; i < tables.items.length; i++) {
          const table = tables.items[i];
          table.load(["rowCount", "values", "font", "style"]);
          await context.sync();

          tableInfos.push({
            index: i,
            rowCount: table.rowCount,
            columnCount: table.values[0]?.length || 0,
            firstRowPreview: table.values[0]?.map((cell: string) => cell.substring(0, 20)) || []
          });
        }

        return {
          totalTables: tables.items.length,
          tables: tableInfos,
          message: tables.items.length > 0
            ? `Found ${tables.items.length} table(s)`
            : "No tables in document"
        };
      });

      console.log("[DEBUG] get_table_info result:", result);
      setDebugInfo(result);
    } catch (error) {
      console.error("[DEBUG] Error:", error);
      setDebugInfo({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      setDebugLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isStreaming) return;

    // Récupérer la sélection automatiquement
    const selectedText = await getCurrentSelection();

    if (selectedText && selectedText.trim().length > 0) {
      // Si du texte est sélectionné, l'ajouter au contexte
      sendMessage(
        input,
        `[Texte sélectionné dans le document]\n${selectedText}`
      );
    } else if (includeFullDocument) {
      // Si l'option "inclure le document complet" est cochée
      const fullText = await getFullDocumentContent();
      if (fullText && fullText.trim().length > 0) {
        sendMessage(input, `[Contenu complet du document]\n${fullText}`);
      } else {
        sendMessage(input);
      }
    } else {
      // Sinon, envoyer le message simple
      sendMessage(input);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#12151C",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid rgba(124, 93, 250, 0.12)",
          background: "rgba(20, 22, 37, 0.95)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: "flex-start",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                color: "#7C5DFA",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {agentId === "devana" ? "Devana" : agentId}
            </div>
          </div>
          {wsConnected ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "2px 6px",
                background: "rgba(72, 199, 116, 0.15)",
                border: "1px solid rgba(72, 199, 116, 0.3)",
                borderRadius: "4px",
                fontSize: "10px",
                color: "#48C774",
              }}
              title={`Agent mode activé - Session: ${wordSessionId}`}
            >
              <span
                style={{
                  fontSize: "8px",
                  animation:
                    wsConnectionAttempts > 1 ? "pulse 2s ease-in-out" : "none",
                }}
              >
                ●
              </span>
              <span>Agent</span>
              {wsConnectionAttempts > 1 && (
                <span style={{ fontSize: "8px", opacity: 0.7 }}>
                  (R{wsConnectionAttempts - 1})
                </span>
              )}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "2px 6px",
                background:
                  wsConnectionAttempts > 1
                    ? "rgba(255, 200, 87, 0.15)"
                    : "rgba(255, 146, 146, 0.15)",
                border:
                  wsConnectionAttempts > 1
                    ? "1px solid rgba(255, 200, 87, 0.3)"
                    : "1px solid rgba(255, 146, 146, 0.3)",
                borderRadius: "4px",
                fontSize: "10px",
                color: wsConnectionAttempts > 1 ? "#FFC857" : "#FF9292",
              }}
              title={
                wsConnectionAttempts > 1
                  ? `Reconnexion en cours... (${wsConnectionAttempts})`
                  : wsConnectionError || "WebSocket non connecté"
              }
            >
              <span
                style={{
                  fontSize: "8px",
                  animation:
                    wsConnectionAttempts > 1
                      ? "spin 1s linear infinite"
                      : "none",
                }}
              >
                ●
              </span>
              <span>
                {wsConnectionAttempts > 1 ? "Reconnexion..." : "Déconnecté"}
              </span>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              style={{
                padding: "6px 12px",
                background: "rgba(124, 93, 250, 0.15)",
                border: "1px solid rgba(124, 93, 250, 0.3)",
                borderRadius: "6px",
                color: "#7C5DFA",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "rgba(124, 93, 250, 0.25)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(124, 93, 250, 0.15)";
              }}
              title="Paramètres"
            >
              ⚙️ Paramètres
            </button>
          )}

          {onLogout && (
            <button
              onClick={onLogout}
              style={{
                padding: "6px 12px",
                background: "rgba(255, 146, 146, 0.15)",
                border: "1px solid rgba(255, 146, 146, 0.3)",
                borderRadius: "6px",
                color: "#FF9292",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "rgba(255, 146, 146, 0.25)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(255, 146, 146, 0.15)";
              }}
              title="Se déconnecter"
            >
              🚪 Logout
            </button>
          )}
        </div>
      </div>

      {/* Debug WebSocket Panel */}
      <div
        style={{
          padding: "8px 16px",
          background: "rgba(20, 22, 37, 0.95)",
          borderBottom: "1px solid rgba(124, 93, 250, 0.12)",
          fontSize: "11px",
        }}
      >
        {showDebug && (
          <details style={{ color: "rgba(255, 255, 255, 0.7)" }}>
            <summary
              style={{
                cursor: "pointer",
                userSelect: "none",
                marginBottom: "8px",
              }}
            >
              🔍 Debug WebSocket
            </summary>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              padding: "8px",
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
              fontFamily: "monospace",
            }}
          >
            <div>
              <strong>État:</strong>{" "}
              {wsConnected ? "✅ Connecté" : "❌ Déconnecté"}
            </div>
            <div>
              <strong>Session ID:</strong> {wordSessionId || "N/A"}
            </div>
            <div>
              <strong>API URL:</strong> {apiUrl || "N/A"}
            </div>
            <div>
              <strong>WS URL:</strong>{" "}
              {apiUrl ? apiUrl.replace(/^http/, "ws") + "/ws/tools" : "N/A"}
            </div>
            <div>
              <strong>Dynamic Tools:</strong>{" "}
              {wordClient?.getToolsConfig().length || 0} tools configurés
            </div>
            <div>
              <strong>Tentatives:</strong> {wsConnectionAttempts}
            </div>
            {wsConnectionError && (
              <div style={{ color: "#FF9292", marginTop: "4px" }}>
                <strong>Erreur:</strong> {wsConnectionError}
              </div>
            )}
          </div>
          </details>
        )}

        {showDebug && (
          <details
            style={{ color: "rgba(255, 255, 255, 0.7)", marginTop: "8px" }}
          >
            <summary
              style={{
                cursor: "pointer",
                userSelect: "none",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              📋 Logs en Temps Réel ({logs.length})
          </summary>
          <div
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              padding: "8px",
              background: "rgba(0, 0, 0, 0.3)",
              borderRadius: "6px",
              fontFamily: "monospace",
              fontSize: "10px",
            }}
          >
            {logs.length === 0 ? (
              <div
                style={{
                  color: "rgba(255, 255, 255, 0.5)",
                  fontStyle: "italic",
                }}
              >
                Aucun log pour le moment...
              </div>
            ) : (
              logs.map((log, i) => (
                <div
                  key={i}
                  style={{
                    padding: "4px",
                    marginBottom: "2px",
                    borderLeft: `2px solid ${
                      log.level === "success"
                        ? "#48C774"
                        : log.level === "error"
                        ? "#FF9292"
                        : "#7C5DFA"
                    }`,
                    paddingLeft: "8px",
                    color:
                      log.level === "success"
                        ? "#48C774"
                        : log.level === "error"
                        ? "#FF9292"
                        : "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <span style={{ color: "rgba(255, 255, 255, 0.5)" }}>
                    [{log.time}]
                  </span>{" "}
                  {log.message}
                </div>
              ))
            )}
          </div>
          <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
            <button
              onClick={() => setLogs([])}
              style={{
                flex: 1,
                padding: "4px",
                background: "rgba(255, 146, 146, 0.15)",
                border: "1px solid rgba(255, 146, 146, 0.3)",
                borderRadius: "4px",
                color: "#FF9292",
                fontSize: "10px",
                cursor: "pointer",
              }}
            >
              Effacer
            </button>
            <button
              onClick={async () => {
                if (!wordClient) {
                  addLog("error", "❌ Client non connecté");
                  return;
                }

                addLog("info", "🧪 Démarrage des tests automatisés...");
                addLog("info", "");
                let passedTests = 0;
                let failedTests = 0;

                // Test 1: get_document_info
                try {
                  addLog("info", "1️⃣ Test: get_document_info");
                  const tool = wordClient
                    .getToolsConfig()
                    .find((t) => t.name === "get_document_info");
                  if (tool) {
                    const handler = (wordClient as any).tools.get(
                      "get_document_info"
                    ).handler;
                    const result: any = await handler({});
                    if (result.totalParagraphs >= 0 && result.totalWords >= 0) {
                      addLog(
                        "success",
                        `  ✅ OK - ${result.totalParagraphs} paragraphes, ${result.totalWords} mots`
                      );
                      passedTests++;
                    } else {
                      throw new Error("Invalid result structure");
                    }
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 2: add_paragraph
                try {
                  addLog("info", "2️⃣ Test: add_paragraph");
                  const handler = (wordClient as any).tools.get(
                    "add_paragraph"
                  ).handler;
                  await handler({
                    text: "TEST - Paragraphe de test",
                    style: "Normal",
                  });

                  // Vérifier que ça a été ajouté
                  const verifyHandler = (wordClient as any).tools.get(
                    "get_document_info"
                  ).handler;
                  const before: any = await verifyHandler({});
                  await handler({
                    text: "TEST - Second paragraphe",
                    style: "Normal",
                  });
                  const after: any = await verifyHandler({});

                  if (after.totalParagraphs > before.totalParagraphs) {
                    addLog(
                      "success",
                      `  ✅ OK - Paragraphe ajouté (${before.totalParagraphs} → ${after.totalParagraphs})`
                    );
                    passedTests++;
                  } else {
                    throw new Error("Paragraph count did not increase");
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 3: add_list_item
                try {
                  addLog("info", "3️⃣ Test: add_list_item");
                  const handler = (wordClient as any).tools.get(
                    "add_list_item"
                  ).handler;
                  await handler({ text: "Item 1", listType: "bullet" });
                  await handler({ text: "Item 2", listType: "bullet" });
                  addLog("success", "  ✅ OK - Liste créée");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 4: apply_text_style
                try {
                  addLog("info", "4️⃣ Test: apply_text_style");
                  const handler = (wordClient as any).tools.get(
                    "apply_text_style"
                  ).handler;
                  await handler({ paragraphNumber: 1, style: "Title" });
                  addLog(
                    "success",
                    "  ✅ OK - Style Title appliqué au paragraphe 1"
                  );
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 5: format_text
                try {
                  addLog("info", "5️⃣ Test: format_text");
                  const handler = (wordClient as any).tools.get(
                    "format_text"
                  ).handler;
                  await handler({
                    paragraphNumber: 2,
                    bold: true,
                    fontSize: 14,
                  });
                  addLog("success", "  ✅ OK - Formatage appliqué");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 6: search
                try {
                  addLog("info", "6️⃣ Test: search");
                  const handler = (wordClient as any).tools.get(
                    "search"
                  ).handler;
                  const results: any = await handler({ searchText: "TEST" });
                  if (Array.isArray(results)) {
                    addLog(
                      "success",
                      `  ✅ OK - Trouvé ${results.length} occurrence(s)`
                    );
                    passedTests++;
                  } else {
                    throw new Error("Invalid search result");
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 7: insert_table
                try {
                  addLog("info", "7️⃣ Test: insert_table");
                  const handler = (wordClient as any).tools.get(
                    "insert_table"
                  ).handler;
                  await handler({
                    afterParagraphNumber: 2,
                    rows: 3,
                    columns: 2,
                    headerRow: true,
                    data: [
                      ["Col1", "Col2"],
                      ["A", "B"],
                      ["C", "D"],
                    ],
                  });
                  addLog("success", "  ✅ OK - Tableau 3x2 créé");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 8: add_comment
                try {
                  addLog("info", "8️⃣ Test: add_comment");
                  const handler = (wordClient as any).tools.get(
                    "add_comment"
                  ).handler;
                  await handler({
                    paragraphNumber: 1,
                    commentText: "Test comment",
                  });
                  addLog("success", "  ✅ OK - Commentaire ajouté");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 9: get_comments
                try {
                  addLog("info", "9️⃣ Test: get_comments");
                  const handler = (wordClient as any).tools.get(
                    "get_comments"
                  ).handler;
                  const comments: any = await handler({});
                  if (Array.isArray(comments)) {
                    addLog(
                      "success",
                      `  ✅ OK - ${comments.length} commentaire(s) trouvé(s)`
                    );
                    passedTests++;
                  } else {
                    throw new Error("Invalid comments result");
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 10: add_empty_line
                try {
                  addLog("info", "🔟 Test: add_empty_line");
                  const handler = (wordClient as any).tools.get(
                    "add_empty_line"
                  ).handler;
                  await handler({ count: 2 });
                  addLog("success", "  ✅ OK - Lignes vides ajoutées");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 11: align_paragraph
                try {
                  addLog("info", "1️⃣1️⃣ Test: align_paragraph");
                  const handler = (wordClient as any).tools.get(
                    "align_paragraph"
                  ).handler;
                  await handler({ paragraphNumber: 1, alignment: "center" });
                  addLog("success", "  ✅ OK - Paragraphe centré");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 12: edit_content (replace)
                try {
                  addLog("info", "1️⃣2️⃣ Test: edit_content (replaceParagraph)");
                  const handler = (wordClient as any).tools.get(
                    "edit_content"
                  ).handler;
                  await handler({
                    operation: "replaceParagraph",
                    content: "Paragraphe remplacé par test",
                    paragraphNumber: 2,
                  });
                  addLog("success", "  ✅ OK - Paragraphe 2 remplacé");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 13: get_content
                try {
                  addLog("info", "1️⃣3️⃣ Test: get_content");
                  const handler = (wordClient as any).tools.get(
                    "get_content"
                  ).handler;
                  const content: any = await handler({});
                  if (typeof content === "string" && content.length > 0) {
                    addLog(
                      "success",
                      `  ✅ OK - Contenu récupéré (${content.length} caractères)`
                    );
                    passedTests++;
                  } else {
                    throw new Error("No content returned");
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 14: search_replace
                try {
                  addLog("info", "1️⃣4️⃣ Test: search_replace");
                  const handler = (wordClient as any).tools.get(
                    "search_replace"
                  ).handler;
                  const result: any = await handler({
                    searchText: "TEST",
                    replaceText: "TESTÉ",
                    replaceAll: true,
                  });
                  addLog(
                    "success",
                    `  ✅ OK - ${result.count} remplacement(s)`
                  );
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 15: get_table_info
                try {
                  addLog("info", "1️⃣5️⃣ Test: get_table_info");
                  const handler = (wordClient as any).tools.get(
                    "get_table_info"
                  ).handler;
                  const tables: any = await handler({});
                  if (Array.isArray(tables)) {
                    addLog(
                      "success",
                      `  ✅ OK - ${tables.length} tableau(x) trouvé(s)`
                    );
                    passedTests++;
                  } else {
                    throw new Error("Invalid table info");
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 16: edit_table_cell
                try {
                  addLog("info", "1️⃣6️⃣ Test: edit_table_cell");
                  const handler = (wordClient as any).tools.get(
                    "edit_table_cell"
                  ).handler;
                  await handler({
                    tableIndex: 0,
                    rowIndex: 0,
                    columnIndex: 0,
                    content: "MODIFIÉ",
                  });
                  addLog("success", "  ✅ OK - Cellule [0,0] modifiée");
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 17: add_comment_on_text
                try {
                  addLog("info", "1️⃣7️⃣ Test: add_comment_on_text");
                  const handler = (wordClient as any).tools.get(
                    "add_comment_on_text"
                  ).handler;
                  await handler({
                    searchText: "TESTÉ",
                    commentText: "Mot commenté",
                  });
                  addLog("success", '  ✅ OK - Commentaire sur "TESTÉ"');
                  passedTests++;
                } catch (e) {
                  addLog(
                    "error",
                    `  ❌ FAIL: ${e instanceof Error ? e.message : e}`
                  );
                  failedTests++;
                }

                // Test 18: get_selection (skip - nécessite sélection manuelle)
                addLog(
                  "info",
                  "1️⃣8️⃣ Test: get_selection - SKIPPED (nécessite sélection manuelle)"
                );

                // Test 19: delete_comment (skip - nécessite ID de get_comments)
                addLog(
                  "info",
                  "1️⃣9️⃣ Test: delete_comment - SKIPPED (testé via get_comments)"
                );

                // Test 20: insert_image (skip - nécessite URL valide)
                addLog(
                  "info",
                  "2️⃣0️⃣ Test: insert_image - SKIPPED (nécessite URL publique)"
                );

                // Résumé
                addLog("info", "");
                addLog("info", "═══════════════════════════");
                addLog("success", `✅ Tests réussis: ${passedTests}`);
                if (failedTests > 0) {
                  addLog("error", `❌ Tests échoués: ${failedTests}`);
                }
                addLog("info", `📊 Total: ${passedTests + failedTests} tests`);
                addLog("info", "═══════════════════════════");
              }}
              style={{
                flex: 1,
                padding: "4px",
                background: "rgba(72, 199, 116, 0.15)",
                border: "1px solid rgba(72, 199, 116, 0.3)",
                borderRadius: "4px",
                color: "#48C774",
                fontSize: "10px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              🧪 Tests
            </button>
          </div>
          </details>
        )}

        {showDebug && (
          <details
          style={{ color: "rgba(255, 255, 255, 0.7)", marginTop: "8px" }}
        >
          <summary
            style={{
              cursor: "pointer",
              userSelect: "none",
              marginBottom: "8px",
              fontWeight: "600",
            }}
          >
            🌐 Sessions Actives ({sessions.length})
          </summary>
          <div
            style={{
              padding: "8px",
              background: "rgba(0, 0, 0, 0.3)",
              borderRadius: "6px",
              fontFamily: "monospace",
              fontSize: "10px",
            }}
          >
            {sessions.length === 0 ? (
              <div
                style={{
                  color: "rgba(255, 255, 255, 0.5)",
                  fontStyle: "italic",
                }}
              >
                Aucune autre session détectée. Ouvre un autre document Word pour
                tester le multi-documents.
              </div>
            ) : (
              sessions.map((session, i) => (
                <div
                  key={i}
                  style={{
                    padding: "6px",
                    marginBottom: "4px",
                    background: session.isCurrent
                      ? "rgba(72, 199, 116, 0.1)"
                      : "rgba(124, 93, 250, 0.1)",
                    border: `1px solid ${
                      session.isCurrent ? "#48C774" : "#7C5DFA"
                    }`,
                    borderRadius: "4px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      color: session.isCurrent ? "#48C774" : "#7C5DFA",
                    }}
                  >
                    {session.isCurrent && "➤ "}
                    {session.documentName || "Unnamed"}
                  </div>
                  <div
                    style={{
                      fontSize: "9px",
                      color: "rgba(255, 255, 255, 0.5)",
                      marginTop: "2px",
                    }}
                  >
                    Session: {session.sessionId.substring(0, 30)}...
                  </div>
                  <div
                    style={{
                      fontSize: "9px",
                      color: "rgba(255, 255, 255, 0.5)",
                    }}
                  >
                    Tools: {session.toolsCount || 0}
                  </div>
                </div>
              ))
            )}
            <button
              onClick={async () => {
                if (!wordClient) {
                  addLog("error", "❌ Client non connecté");
                  return;
                }

                try {
                  addLog("info", "🔄 Rafraîchissement des sessions...");

                  // Appeler l'endpoint REST /sessions/list
                  const response = await fetch(`${apiUrl}/sessions/list`, {
                    headers: {
                      "x-session-id": sessionIdRef.current || "",
                    },
                  });

                  if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                  }

                  const data = await response.json();

                  if (data.success && data.sessions) {
                    setSessions(data.sessions);
                    addLog(
                      "success",
                      `✅ ${data.sessions.length} session(s) trouvée(s)`
                    );
                    console.log("[Sessions] List:", data.sessions);
                  } else {
                    throw new Error("Invalid response");
                  }
                } catch (e) {
                  addLog(
                    "error",
                    `❌ Erreur: ${e instanceof Error ? e.message : e}`
                  );
                }
              }}
              style={{
                width: "100%",
                padding: "4px",
                marginTop: "8px",
                background: "rgba(124, 93, 250, 0.15)",
                border: "1px solid rgba(124, 93, 250, 0.3)",
                borderRadius: "4px",
                color: "#7C5DFA",
                fontSize: "10px",
                cursor: "pointer",
              }}
            >
              🔄 Rafraîchir les sessions
            </button>
          </div>
          </details>
        )}
      </div>

      {/* Actions rapides - visibles uniquement si du texte est sélectionné */}
      {selectionWordCount > 0 && (
        <div
          style={{
            padding: "12px 16px",
            background: "rgba(20, 22, 37, 0.95)",
            borderBottom: "1px solid rgba(124, 93, 250, 0.12)",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {allQuickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleQuickAction(action)}
              style={{
                padding: "8px 12px",
                background: "rgba(124, 93, 250, 0.15)",
                border: "1px solid rgba(124, 93, 250, 0.3)",
                borderRadius: "6px",
                color: "#7C5DFA",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "rgba(124, 93, 250, 0.25)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(124, 93, 250, 0.15)";
              }}
            >
              <span>{action.icon}</span>
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {messages.length === 0 && !isStreaming && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              padding: "32px",
              opacity: 0.6,
            }}
          >
            <h3 style={{ marginBottom: "8px", color: "#7C5DFA" }}>Devana</h3>
            <p style={{ fontSize: "14px", lineHeight: "1.5" }}>
              Comment puis-je vous aider aujourd'hui ?
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <MessageItem
            key={idx}
            message={msg}
            onInsert={
              msg.role === "assistant" ? insertTextInDocument : undefined
            }
            onReplace={
              msg.role === "assistant" ? replaceSelectionInDocument : undefined
            }
          />
        ))}

        {isStreaming && streamedContent && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                marginBottom: "8px",
                color: "#48C774",
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Devana
            </div>
            <div
              className="markdown-content"
              style={{ wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{
                __html: marked.parse(parseToolTags(streamedContent)) as string,
              }}
            />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          padding: "16px",
          borderTop: "1px solid rgba(124, 93, 250, 0.12)",
          background: "rgba(20, 22, 37, 0.95)",
        }}
      >
        {messages.length > 0 && !isStreaming && (
          <button
            onClick={startNewConversation}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "8px",
              background: "rgba(124, 93, 250, 0.15)",
              border: "1px solid rgba(124, 93, 250, 0.3)",
              borderRadius: "8px",
              color: "#7C5DFA",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "rgba(124, 93, 250, 0.25)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "rgba(124, 93, 250, 0.15)";
            }}
          >
            🔄 Nouvelle conversation
          </button>
        )}

        {isStreaming && (
          <button
            onClick={stopGeneration}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "8px",
              background: "#FF9292",
              border: "none",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ⏹ Arrêter la génération
          </button>
        )}

        {/* Chip du compteur de mots sélectionnés */}
        {selectionWordCount > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              marginBottom: "8px",
              background: "rgba(72, 199, 116, 0.15)",
              border: "1px solid rgba(72, 199, 116, 0.3)",
              borderRadius: "16px",
              fontSize: "12px",
              color: "#48C774",
              fontWeight: 600,
            }}
          >
            <span>📄</span>
            <span>
              {selectionWordCount} mot{selectionWordCount > 1 ? "s" : ""}{" "}
              sélectionné{selectionWordCount > 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Checkbox pour inclure le document complet (si aucune sélection) */}
        {selectionWordCount === 0 && fullDocumentWordCount > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              marginBottom: "8px",
              background: includeFullDocument
                ? "rgba(124, 93, 250, 0.15)"
                : "rgba(255, 255, 255, 0.05)",
              border: `1px solid ${
                includeFullDocument
                  ? "rgba(124, 93, 250, 0.3)"
                  : "rgba(255, 255, 255, 0.1)"
              }`,
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onClick={() => setIncludeFullDocument(!includeFullDocument)}
          >
            <input
              type="checkbox"
              checked={includeFullDocument}
              onChange={(e) => setIncludeFullDocument(e.target.checked)}
              style={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
              }}
            />
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: includeFullDocument
                    ? "#7C5DFA"
                    : "rgba(255, 255, 255, 0.9)",
                }}
              >
                Inclure le document complet
              </span>
              <span
                style={{ fontSize: "11px", color: "rgba(255, 255, 255, 0.6)" }}
              >
                📄 {fullDocumentWordCount} mot
                {fullDocumentWordCount > 1 ? "s" : ""} dans le document
              </span>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "flex-end",
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Posez votre question..."
            disabled={isStreaming}
            style={{
              flex: 1,
              padding: "12px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(124, 93, 250, 0.3)",
              borderRadius: "12px",
              color: "white",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "none",
              minHeight: "48px",
              maxHeight: "120px",
              outline: "none",
            }}
            rows={1}
          />
          {showDebug && (
            <button
              onClick={handleDebugClick}
              title="Debug: Voir get_document_info"
              style={{
                padding: "12px",
                background: "rgba(255, 165, 0, 0.2)",
                border: "1px solid rgba(255, 165, 0, 0.4)",
                borderRadius: "12px",
                color: "orange",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s",
                height: "48px",
                marginRight: "8px",
              }}
            >
              🐛
            </button>
          )}
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || isStreaming}
            style={{
              padding: "12px 20px",
              background:
                input.trim() && !isStreaming
                  ? "linear-gradient(135deg, #7C5DFA 0%, #6A4DE8 100%)"
                  : "rgba(124, 93, 250, 0.3)",
              border: "none",
              borderRadius: "12px",
              color: "white",
              fontSize: "14px",
              fontWeight: 600,
              cursor: input.trim() && !isStreaming ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              height: "48px",
            }}
          >
            Envoyer
          </button>
        </div>
      </div>

      {/* Modal de debug */}
      {showDebugModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => setShowDebugModal(false)}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "2px solid orange",
              borderRadius: "12px",
              padding: "20px",
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
              color: "white",
              fontFamily: "monospace",
              minWidth: "600px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: "orange", marginBottom: "20px" }}>
              🐛 Debug Tools - Tester les fonctions Word
            </h2>

            {/* Boutons pour tester chaque fonction */}
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ color: "#7C5DFA" }}>🔧 Fonctions disponibles:</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "20px" }}>
                <button
                  onClick={testGetDocumentInfo}
                  disabled={debugLoading}
                  style={{
                    padding: "10px",
                    background: currentDebugFunction === "get_document_info" ? "#7C5DFA" : "rgba(124, 93, 250, 0.2)",
                    border: "1px solid #7C5DFA",
                    borderRadius: "6px",
                    color: "white",
                    cursor: debugLoading ? "wait" : "pointer",
                    fontSize: "12px",
                  }}
                >
                  📊 get_document_info()
                </button>
                <button
                  onClick={testGetContent}
                  disabled={debugLoading}
                  style={{
                    padding: "10px",
                    background: currentDebugFunction === "get_content" ? "#7C5DFA" : "rgba(124, 93, 250, 0.2)",
                    border: "1px solid #7C5DFA",
                    borderRadius: "6px",
                    color: "white",
                    cursor: debugLoading ? "wait" : "pointer",
                    fontSize: "12px",
                  }}
                >
                  📄 get_content()
                </button>
                <button
                  onClick={testGetSelection}
                  disabled={debugLoading}
                  style={{
                    padding: "10px",
                    background: currentDebugFunction === "get_selection" ? "#7C5DFA" : "rgba(124, 93, 250, 0.2)",
                    border: "1px solid #7C5DFA",
                    borderRadius: "6px",
                    color: "white",
                    cursor: debugLoading ? "wait" : "pointer",
                    fontSize: "12px",
                  }}
                >
                  ✂️ get_selection()
                </button>
                <button
                  onClick={testGetTableInfo}
                  disabled={debugLoading}
                  style={{
                    padding: "10px",
                    background: currentDebugFunction === "get_table_info" ? "#7C5DFA" : "rgba(124, 93, 250, 0.2)",
                    border: "1px solid #7C5DFA",
                    borderRadius: "6px",
                    color: "white",
                    cursor: debugLoading ? "wait" : "pointer",
                    fontSize: "12px",
                  }}
                >
                  📊 get_table_info()
                </button>
              </div>
            </div>

            {/* Indicateur de chargement */}
            {debugLoading && (
              <div style={{ textAlign: "center", marginBottom: "20px", color: "#7C5DFA" }}>
                ⏳ Test en cours...
              </div>
            )}

            {/* Affichage du résultat */}
            {debugInfo && (
              <div style={{ marginBottom: "20px" }}>
                <h3 style={{ color: "#7C5DFA" }}>
                  📋 Résultat: {currentDebugFunction || "Cliquez sur un bouton pour tester"}
                </h3>

                {/* Affichage d'erreur */}
                {debugInfo.error && (
                  <div style={{ background: "rgba(255, 0, 0, 0.2)", padding: "10px", borderRadius: "6px", marginBottom: "10px" }}>
                    ❌ Erreur: {debugInfo.error}
                  </div>
                )}

                {/* Affichage personnalisé selon la fonction */}
                {!debugInfo.error && currentDebugFunction === "get_document_info" && (
                  <>
                    <div style={{ background: "rgba(0,0,0,0.5)", padding: "10px", borderRadius: "6px", marginBottom: "10px" }}>
                      <div>📄 Pages: {debugInfo.totalPages}</div>
                      <div>📝 Paragraphes: {debugInfo.totalParagraphs}</div>
                      <div>📖 Mots: {debugInfo.totalWords}</div>
                      <div>🔤 Caractères: {debugInfo.totalCharacters}</div>
                      <div>📋 Lignes: {debugInfo.totalLines}</div>
                      <div>📊 Tableaux: {debugInfo.totalTables}</div>
                    </div>
                    {debugInfo.paragraphPreviews && (
                      <div style={{ maxHeight: "200px", overflow: "auto" }}>
                        {debugInfo.paragraphPreviews.slice(0, 5).map((para: any, i: number) => (
                          <div key={i} style={{ fontSize: "11px", marginBottom: "5px", padding: "5px", background: "rgba(0,0,0,0.3)", borderRadius: "4px" }}>
                            Para #{para.number}: {para.preview}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {!debugInfo.error && currentDebugFunction === "get_content" && debugInfo.preview && (
                  <div style={{ maxHeight: "300px", overflow: "auto" }}>
                    {debugInfo.preview.map((para: any, i: number) => (
                      <div key={i} style={{ marginBottom: "10px", padding: "10px", background: "rgba(0,0,0,0.5)", borderRadius: "6px" }}>
                        <div style={{ color: "#7C5DFA", fontSize: "11px", marginBottom: "5px" }}>
                          Paragraphe #{para.number} | Style: {para.style}
                        </div>
                        <div style={{ fontSize: "12px", marginBottom: "5px" }}>
                          {para.text}
                        </div>
                        <div style={{ fontSize: "10px", color: "#888" }}>
                          Font: {para.font.name} {para.font.size}pt
                          {para.font.bold && " Bold"}
                          {para.font.italic && " Italic"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!debugInfo.error && currentDebugFunction === "get_selection" && (
                  <div style={{ background: "rgba(0,0,0,0.5)", padding: "10px", borderRadius: "6px" }}>
                    <div>📝 Texte: "{debugInfo.text}"</div>
                    <div>📏 Longueur: {debugInfo.length} caractères</div>
                    {debugInfo.style && <div>🎨 Style: {debugInfo.style}</div>}
                    {debugInfo.font && (
                      <div>
                        🔤 Police: {debugInfo.font.name} {debugInfo.font.size}pt
                        {debugInfo.font.bold && " Bold"}
                        {debugInfo.font.italic && " Italic"}
                      </div>
                    )}
                  </div>
                )}

                {!debugInfo.error && currentDebugFunction === "get_table_info" && (
                  <div>
                    <div style={{ background: "rgba(0,0,0,0.5)", padding: "10px", borderRadius: "6px", marginBottom: "10px" }}>
                      Total: {debugInfo.totalTables} table(s)
                    </div>
                    {debugInfo.tables && debugInfo.tables.map((table: any, i: number) => (
                      <div key={i} style={{ marginBottom: "10px", padding: "10px", background: "rgba(0,0,0,0.3)", borderRadius: "6px" }}>
                        <div>Table #{table.index + 1}</div>
                        <div>{table.rowCount} lignes × {table.columnCount} colonnes</div>
                        {table.firstRowPreview && (
                          <div style={{ fontSize: "11px", marginTop: "5px" }}>
                            En-tête: {table.firstRowPreview.join(" | ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* JSON brut toujours disponible */}
                <div style={{ marginTop: "20px" }}>
                  <h4 style={{ color: "#7C5DFA", fontSize: "12px", marginBottom: "10px" }}>
                    💻 JSON complet:
                  </h4>
                  <pre
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      padding: "10px",
                      borderRadius: "6px",
                      fontSize: "10px",
                      overflow: "auto",
                      maxHeight: "150px",
                    }}
                  >
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {!debugInfo && !debugLoading && (
              <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
                Cliquez sur un bouton ci-dessus pour tester une fonction
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
              <button
                onClick={() => setShowDebugModal(false)}
                style={{
                  padding: "10px 20px",
                  background: "orange",
                  border: "none",
                  borderRadius: "6px",
                  color: "black",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
